import { SessionUser } from 'model/userSession';
import {
  API_BASE_URL,
  ApiEnvelope,
  DsEntry,
  MusicScanResult,
  ThumbnailItem,
  apiClient,
  authHeaders,
} from 'service/apiClient';
import localStorageService from 'service/localStorageService';
import { beginTransfer, endTransfer } from 'service/sessionActivity';

export type { DsEntry };

function withTransfer<T>(p: Promise<T>): Promise<T> {
  beginTransfer();
  return p.finally(() => endTransfer());
}

const fileBrowserApi = {
  whoAmI() {
    return apiClient.get<ApiEnvelope<SessionUser>>('/auth/who-am-i', authHeaders());
  },

  dsLs(path: string, opts?: { hardFresh?: boolean }) {
    return apiClient.post<ApiEnvelope<DsEntry[]>>(
      '/ds/ls',
      { path, hardFresh: opts?.hardFresh === true },
      authHeaders()
    );
  },

  dsMkdir(path: string) {
    return apiClient.post<ApiEnvelope<DsEntry>>('/ds/mkdir', { path }, authHeaders());
  },

  dsDelete(path: string, recursive = true) {
    return apiClient.post<ApiEnvelope<null>>(
      '/ds/delete',
      { path, recursive },
      { ...authHeaders(), timeout: 0 }
    );
  },

  dsCopy(from: string, to: string) {
    return withTransfer(
      apiClient.post<ApiEnvelope<DsEntry>>(
        '/ds/copy',
        { from, to },
        { ...authHeaders(), timeout: 0 }
      )
    );
  },

  dsMove(from: string, to: string) {
    return withTransfer(
      apiClient.post<ApiEnvelope<DsEntry>>(
        '/ds/move',
        { from, to },
        { ...authHeaders(), timeout: 0 }
      )
    );
  },

  dsDu(paths: string[]) {
    return apiClient.post<ApiEnvelope<{ bytes: number; files: number; folders: number }>>(
      '/ds/du',
      { paths },
      { ...authHeaders(), timeout: 0 }
    );
  },

  dsScanMusic(paths: string[]) {
    return apiClient.post<ApiEnvelope<MusicScanResult>>(
      '/ds/scan-music',
      { paths },
      { ...authHeaders(), timeout: 0 }
    );
  },

  dsZip(paths: string[], destDir: string) {
    return apiClient.post<ApiEnvelope<DsEntry>>(
      '/ds/zip',
      { paths, destDir },
      { ...authHeaders(), timeout: 0 }
    );
  },

  tagsAdd(paths: string[], tags: string[]) {
    return apiClient.post<ApiEnvelope<number>>('/ds/tags', { paths, tags }, authHeaders());
  },

  tagsRemove(paths: string[], tags: string[]) {
    return apiClient.delete<ApiEnvelope<number>>('/ds/tags', {
      ...authHeaders(),
      data: { paths, tags },
    });
  },

  tagsInPath(path: string) {
    return apiClient.get<ApiEnvelope<string[]>>('/ds/tags', {
      ...authHeaders(),
      params: { path },
    });
  },

  tagsFindPaths(path: string, tags: string[]) {
    return apiClient.get<ApiEnvelope<DsEntry[]>>('/ds/tags/paths', {
      ...authHeaders(),
      params: { path, tag: tags },
      paramsSerializer: (params) => {
        const search = new URLSearchParams();
        search.set('path', String(params.path ?? ''));
        const list = params.tag;
        if (Array.isArray(list)) {
          for (const t of list) {
            search.append('tag', String(t));
          }
        } else if (list != null) {
          search.append('tag', String(list));
        }
        return search.toString();
      },
    });
  },

  setWallpaper(path: string) {
    return apiClient.put<ApiEnvelope<null>>('/auth/wallpaper', { path }, { ...authHeaders(), timeout: 0 });
  },

  async fetchThumbnails(paths: string[], signal?: AbortSignal) {
    if (paths.length === 0 || paths.length > 3) {
      throw new Error('Thumbnail batch must contain 1–3 paths');
    }
    const res = await apiClient.post<{ items: ThumbnailItem[] }>(
      '/api/files/thumbnails',
      { paths },
      { ...authHeaders(), signal }
    );
    return res.data;
  },

  uploadFile(
    path: string,
    data: Blob,
    opts?: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal }
  ) {
    return apiClient.post<void>('/api/files/upload', data, {
      params: { path },
      headers: {
        Authorization: `Bearer ${localStorageService.getSessionToken().getFullString()}`,
        'Content-Type': 'application/octet-stream',
      },
      timeout: 0,
      signal: opts?.signal,
      transformRequest: [(body) => body],
      onUploadProgress: (ev) => {
        opts?.onProgress?.(ev.loaded, ev.total || data.size);
      },
    });
  },
};

export default fileBrowserApi;
